// @flow

import * as React from 'react';

/** React hook for pre loading an image url. Returns an object with two properties:
 * loadImage: A function which takes in the url to load, and a callback that will be called once the image has loaded
 * isLoaded: A function which checks whether the given url has been previously loaded */
export const useImagePreloader = () => {
  const completedRef = React.useRef<{ [url: string]: boolean }>({});
  const imagesRef = React.useRef<{ [url: string]: Image }>({});

  React.useEffect(
    () => () => {
      // cleanup pending images when unmounting
      Object.entries(imagesRef.current).forEach(([key]) => {
        imagesRef.current[key].onload = null;
        imagesRef.current[key].onerror = null;
        delete imagesRef.current[key];
      });
    },
    [],
  );

  const loadImage = (url: string, onLoaded?: () => void, onError?: () => void) => {
    if (imagesRef.current[url] === undefined) {
      imagesRef.current[url] = new Image();
      imagesRef.current[url].onload = () => {
        if (onLoaded !== undefined) {
          onLoaded();
        }
        completedRef.current[url] = true;
      };
      imagesRef.current[url].onerror = () => {
        if (onError !== undefined) {
          onError();
        }
        completedRef.current[url] = false;
      };
      imagesRef.current[url].src = url;
    }
  };

  const isLoaded = (url: string): boolean => !!completedRef.current[url];

  return { loadImage, isLoaded };
};

type ImagePreloaderProps = {|
  url?: string,
  delay: number,
  loadingRenderer: () => React.Node,
|};

/** A component which pre-loads an image url and renders something in its place while the image is loading */
const ImagePreloaderRenderer = ({ url, delay, loadingRenderer }: ImagePreloaderProps) => {
  const { loadImage, isLoaded } = useImagePreloader();
  const [show, setShow] = React.useState(false);
  const onLoadMemoized = React.useCallback(() => {
    setShow(false);
  }, []);

  React.useEffect(() => {
    let timeout;
    if (url && !isLoaded(url)) {
      loadImage(url, onLoadMemoized);
      if (!show) {
        timeout = setTimeout(() => {
          if (!isLoaded(url)) {
            setShow(true);
          }
        }, delay);
      }
    }
    return () => clearTimeout(timeout);
  }, [url, delay, isLoaded, loadImage, onLoadMemoized, show]);

  if (!url) {
    return null;
  }

  return show ? loadingRenderer() : null;
};

export default ImagePreloaderRenderer;
